import React, { useState, useEffect } from 'react';
import CustomNavbar from './CustomNavbar';
import api from './axiosSetup';
import { FaPlus, FaWrench, FaArrowUp, FaExclamationTriangle, FaBullhorn } from 'react-icons/fa';

const TYPE_CONFIG = {
  new:         { label: 'Neu',           icon: FaPlus,               bg: '#16a34a' },
  fix:         { label: 'Fix',           icon: FaWrench,             bg: '#2563eb' },
  improvement: { label: 'Verbesserung',  icon: FaArrowUp,            bg: '#d97706' },
  breaking:    { label: 'Breaking',      icon: FaExclamationTriangle, bg: '#dc2626' },
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.new;
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
      background: cfg.bg, color: '#fff',
      borderRadius: '0.35rem', padding: '0.15rem 0.55rem',
      fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
      letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>
      <Icon style={{ fontSize: '0.65rem' }} />
      {cfg.label}
    </span>
  );
};

const formatDate = (dt) => {
  if (!dt) return '';
  return new Date(dt).toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
};

const Changelog = () => {
  const [entries, setEntries] = useState([]);
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get('/api/changelog');
        setEntries(data.entries || []);
        setLastSeenAt(data.lastSeenAt ? new Date(data.lastSeenAt) : null);
        api.post('/api/changelog/mark-seen').catch(() => {});
      } catch {}
      setLoading(false);
    };
    load();
  }, []);

  const isNew = (entry) => {
    if (!lastSeenAt) return true;
    return new Date(entry.released_at) > lastSeenAt;
  };

  return (
    <div className="bg-dark text-white" style={{ minHeight: '100vh' }}>
      <CustomNavbar />

      <div className="container py-4" style={{ maxWidth: 760 }}>

        <div className="d-flex align-items-center gap-3 mb-4">
          <div style={{
            width: 44, height: 44, borderRadius: '10px',
            background: 'rgba(250,204,21,0.12)', border: '1.5px solid rgba(250,204,21,0.3)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', flexShrink: 0,
          }}>
            <FaBullhorn style={{ color: '#facc15' }} />
          </div>
          <div>
            <h2 className="mb-0" style={{ fontWeight: 700 }}>Was ist neu?</h2>
            <p className="mb-0 text-secondary" style={{ fontSize: '0.875rem' }}>
              Alle Änderungen und neuen Funktionen der App auf einen Blick.
            </p>
          </div>
        </div>

        {loading && (
          <div className="text-center py-5 text-secondary">
            <div className="spinner-border spinner-border-sm me-2" />
            Wird geladen…
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="text-center py-5 text-secondary">Noch keine Einträge vorhanden.</div>
        )}

        {!loading && entries.map((entry, idx) => {
          const entryIsNew = isNew(entry);
          return (
            <div
              key={entry.id}
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: entryIsNew
                  ? '1px solid rgba(250,204,21,0.35)'
                  : '1px solid rgba(255,255,255,0.08)',
                borderRadius: '1rem',
                padding: '1.5rem',
                marginBottom: idx < entries.length - 1 ? '1rem' : 0,
                position: 'relative',
              }}
            >
              {entryIsNew && (
                <span style={{
                  position: 'absolute', top: '-10px', right: '1.25rem',
                  background: '#facc15', color: '#000',
                  borderRadius: '0.3rem', padding: '0.1rem 0.6rem',
                  fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}>
                  NEU
                </span>
              )}

              <div className="d-flex align-items-start justify-content-between gap-3 mb-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                  <span style={{
                    background: entryIsNew ? 'rgba(250,204,21,0.15)' : 'rgba(255,255,255,0.08)',
                    border: entryIsNew ? '1.5px solid rgba(250,204,21,0.5)' : '1.5px solid rgba(255,255,255,0.15)',
                    borderRadius: '0.5rem', padding: '0.2rem 0.75rem',
                    fontWeight: 700, fontSize: '0.95rem',
                    color: entryIsNew ? '#facc15' : 'rgba(255,255,255,0.7)',
                    fontFamily: 'monospace',
                  }}>
                    v{entry.version}
                  </span>
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: '#fff' }}>
                    {entry.title}
                  </span>
                </div>
                <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                  {formatDate(entry.released_at)}
                </span>
              </div>

              {entry.summary && (
                <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.55)', marginBottom: '1rem' }}>
                  {entry.summary}
                </p>
              )}

              {entry.changes && entry.changes.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                  {entry.changes.map((change, i) => (
                    <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                      <TypeBadge type={change.type} />
                      <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.5 }}>
                        {change.text}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Changelog;
